import React, { useCallback, useContext, useRef } from 'react';
import { Box } from '@src/atoms/Box';
import { Dialog } from '@src/atoms/Dialog';
import { Slider } from '@src/atoms/Slider';
import { Unit } from '@src/core/Unit';
import { IOnBoardingModalSlideItem } from '../_Slide/models';
import { OnBoardingSlide } from '../_Slide/OnBoardingSlide';
import styled from './OnBoardingModal.module.scss';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { Icon } from '@src/atoms/Icon';
import { classnames } from '@src/utils/classnames';
import { Button } from '@src/atoms/Button';

export interface IOnBoardingModalProps {
  localStorageKey: string;
  open: boolean;
  onClose: (isDone: boolean) => void;
  slides: IOnBoardingModalSlideItem[];
  canClose?: boolean;
  disableAdaptiveHeight?: boolean;
}

/**
 * Компонет, реально рисующий модалку и пр., но не отслеживающий свое состояние
 */
export function OnBoardingModal({
  localStorageKey,
  open,
  onClose,
  slides,
  canClose = false,
  disableAdaptiveHeight = false,
}: IOnBoardingModalProps) {
  const sliderRef = useRef(null);
  const { isMobile } = useContext(BreakpointsContext);

  const rootClass = classnames(styled.root, isMobile && styled.mobile);

  const goToSlide = (slideNum: number): void => {
    // @ts-ignore
    sliderRef?.current?.slickGoTo(slideNum);
  };

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleOnBackdropClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onClickPrevBtn = useCallback((idx) => {
    goToSlide(idx - 1);
  }, []);

  const onClickNextBtn = useCallback(
    (idx: number) => {
      if (idx === slides.length - 1) {
        onClose(true);
      } else {
        goToSlide(idx + 1);
      }
    },
    [onClose],
  );

  return (
    <>
      <Dialog
        id="OnBoardingDialog"
        className={rootClass}
        disableCloseOnEscapeKeyDown
        open={open}
        onBackdropClick={handleOnBackdropClick}
        onClose={handleOnClose}
      >
        <Box fullWidth>
          <Unit id={'OnBoardingModal'} env={{ id: localStorageKey }}>
            <Box fullWidth className={styled.wrapper} justifyContent={'center'}>
              {canClose && !isMobile && (
                <Icon name="close" onClick={handleOnClose} className={styled.close} view="basicWhite" size={20} />
              )}
              {canClose && isMobile && (
                <Button
                  preIcon="close"
                  onClick={handleOnClose}
                  className={styled.close}
                  view="actionItem"
                  id="closeOnboarding"
                />
              )}
              <Slider ref={sliderRef} dots fade arrows={false} infinite={false} adaptiveHeight={!disableAdaptiveHeight}>
                {slides?.map((slideItem, idx) => {
                  return (
                    <OnBoardingSlide
                      key={`${idx}`}
                      idx={idx}
                      item={slideItem}
                      onClickPrevBtn={onClickPrevBtn}
                      onClickNextBtn={onClickNextBtn}
                    />
                  );
                })}
              </Slider>
            </Box>
          </Unit>
        </Box>
      </Dialog>
    </>
  );
}
